import * as R from 'ramda';
import { createPortal } from 'react-dom';
import { OuterClick } from 'react-outer-click';
import React, { Component, createRef } from 'react';
// helpers/constants
import * as G from '../helpers';
// ui
import { PopupContentWrapper } from '../ui';
//////////////////////////////////////////////////

const initialState = {
  coords: null,
  opened: false,
  popupComponent: null,
};

const TARGET_RIGHT_GAP = 4;

export const getTargetRightPosition = (params, content, viewportHeight) => {
  const bottomTop = params.bottom + TARGET_RIGHT_GAP;
  const maxTop = Math.max(0, viewportHeight - content.height);

  return {
    margin: 0,
    left: params.right + TARGET_RIGHT_GAP,
    top: Math.max(0, Math.min(bottomTop, maxTop)),
  };
};

export class PopupContent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      coords: {
        top: 0,
        right: 0,
        margin: 0,
      },
    };
  }

  contentRef = createRef();
  
  componentDidMount() {
    const { el, type, minusTop, minusRight } = this.props;

    const params = el?.getBoundingClientRect();
    const content = this.contentRef.current?.getBoundingClientRect();

    this.setPosition({ params, content, type, minusTop, minusRight });
  }

  setPosition({ params, content, type, minusTop, minusRight }) {
    if (R.equals(type, 'target-right')) {
      this.setState({ coords: getTargetRightPosition(params, content, window.innerHeight) });

      return;
    }

    const positionRight = {
      left: params.left,
      right: window.innerWidth - params.right - R.or(minusRight, 0),
      center: window.innerWidth - params.left - (params.width / 2) - (content.width / 2),
    };

    let position = {
      margin: '10px 0 0 0',
      right: positionRight[type],
      top: R.subtract(params.bottom, R.or(minusTop, 10)),
    };

    const expandedContainer = document.getElementsByClassName('react-draggable')[0];

    if (R.and(G.isNotNilAndNotEmpty(expandedContainer), R.equals(type, 'left'))) {
      const windowWidth = window.innerWidth;
      const expandedContainerWidth = expandedContainer.clientWidth;
      const margin = R.subtract(windowWidth, expandedContainerWidth);
      const right = R.subtract(params.left, margin);

      position = R.assoc('right', right, position);
    }

    if (R.lt(R.subtract(window.innerHeight, R.add(params.bottom, content.height)), 60)) {
      const top = params.top - 10 - content.height;

      const openTopParams = {
        margin: '0 0 10px 0',
        top: G.ifElse(R.lt(top, 0), 0, top),
      };

      position = R.mergeRight(position, openTopParams);
    }

    this.setState({ coords: position });
  }

  render() {
    const { type, children, wrapperPadding } = this.props;

    return (
      <PopupContentWrapper
        type={type}
        p={wrapperPadding}
        ref={this.contentRef}
        id='fixed-popover-wrapper'
        positions={this.state.coords}
        {...R.pathOr({}, ['boxStyles'], this.props)}
      >
        {children}
      </PopupContentWrapper>
    );
  }
}

export const withFixedPopover = (
  ComposedComponent,
  withoutScrollHandler = false,
  usePortal = false,
) => {
  class fixedPopover extends Component {
    constructor() {
      super();
      this.handleOpen = this.handleOpen.bind(this);
      this.handleClose = this.handleClose.bind(this);
      this.addScrollHandler = this.addScrollHandler.bind(this);
      this.removeScrollHandler = this.removeScrollHandler.bind(this);
      this.state = initialState;
    }

    addScrollHandler() {
      window.addEventListener('scroll', this.handleClose, true);
    }

    removeScrollHandler() {
      window.removeEventListener('scroll', this.handleClose, true);
    }

    handleOpen(props) {
      const {
        el,
        content,
        position,
        minusTop,
        boxStyles,
        minusRight,
        wrapperPadding,
        shouldNotCloseOnScroll,
      } = props;

      if (shouldNotCloseOnScroll) {
        this.removeScrollHandler();
      } else if (R.not(withoutScrollHandler)) {
        this.addScrollHandler();
      }

      this.setState({
        opened: true,
        popupComponent: (
          <OuterClick onOuterClick={this.handleClose}>
            <PopupContent
              el={el}
              type={position}
              minusTop={minusTop}
              boxStyles={boxStyles}
              minusRight={minusRight}
              wrapperPadding={wrapperPadding}
            >
              {content}
            </PopupContent>
          </OuterClick>
        ),
      });
    }

    handleClose(e) {
      if (R.pathEq('fixed-popover-wrapper', ['target', 'parentNode', 'id'], e)) return;

      this.setState(initialState);
      this.removeScrollHandler();
    }

    render() {
      const { props, handleOpen, handleClose, state: { opened, popupComponent } } = this;

      const { popoverOverflow } = props;

      return (
        <div style={{ overflow: R.or(popoverOverflow, 'unset') }}>
          <ComposedComponent {...props} openFixedPopup={handleOpen} closeFixedPopup={handleClose} />
          {
            opened &&
            usePortal ? createPortal(popupComponent, document.body) : popupComponent
          }
        </div>
      );
    }

  }

  return fixedPopover;
};
